import { useTranslation } from 'react-i18next'
import type { CargaSalida } from '@/types'
import { pickingPct, retrasoDe } from '@/utils/cargasSalida'

/**
 * Los tres datos que deciden si una carga corre prisa: cuándo sale, cuánto se ha
 * preparado y cuánto ocupa.
 *
 * Van como chips y no como texto corrido porque en una lista lateral el ojo
 * escanea formas, no frases: un chip rojo se ve de lejos y «45 d de retraso»
 * escrito en gris no se ve nunca.
 */

type Tono = 'neutro' | 'aviso' | 'alerta' | 'bien'

const TONOS: Record<Tono, { claro: string; oscuro: string }> = {
  neutro: {
    claro: 'bg-slate-100 text-slate-700 ring-slate-200',
    oscuro: 'bg-slate-800 text-slate-300 ring-white/10',
  },
  bien: {
    claro: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    oscuro: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
  },
  aviso: {
    claro: 'bg-amber-50 text-amber-800 ring-amber-200',
    oscuro: 'bg-amber-500/10 text-amber-300 ring-amber-500/30',
  },
  alerta: {
    claro: 'bg-rose-50 text-rose-700 ring-rose-200',
    oscuro: 'bg-rose-500/10 text-rose-300 ring-rose-500/30',
  },
}

export function Chip({ tono = 'neutro', dark, title, children }: {
  tono?: Tono
  dark: boolean
  title?: string
  children: React.ReactNode
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]
        font-semibold tabular-nums ring-1 ring-inset whitespace-nowrap
        ${dark ? TONOS[tono].oscuro : TONOS[tono].claro}`}
    >
      {children}
    </span>
  )
}

/** `dd/mm` de una fecha ISO. */
function ddmm(iso?: string | null): string | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${d}/${m}` : null
}

/** Cuándo sale, y cuánto se pasa. */
export function ChipFecha({ carga, dark }: { carga: CargaSalida; dark: boolean }) {
  const { t } = useTranslation()
  const retraso = retrasoDe(carga)
  const fecha = ddmm(carga.fechaEnvioPrevista)

  if (!fecha) {
    return <Chip tono="aviso" dark={dark} title={t('muelles.chips.sinFechaTooltip')}>
      {t('muelles.chips.sinFecha')}
    </Chip>
  }
  if (retraso !== null) {
    return <Chip tono="alerta" dark={dark} title={t('muelles.chips.retrasoTooltip', { n: retraso })}>
      {fecha} · +{retraso}d
    </Chip>
  }
  return <Chip tono="neutro" dark={dark}>{fecha}</Chip>
}

/** Cuánto se ha preparado. El verde sólo cuando está al 100 %: media verdad no
 *  es una buena noticia. */
export function ChipPicking({ carga, dark }: { carga: CargaSalida; dark: boolean }) {
  const { t } = useTranslation()
  const pct = Math.round(pickingPct(carga))
  const tono: Tono = pct >= 100 ? 'bien' : pct > 0 ? 'aviso' : 'neutro'
  return (
    <Chip tono={tono} dark={dark} title={t('muelles.chips.pickingTooltip', { pct })}>
      <span aria-hidden>▤</span>{pct}%
    </Chip>
  )
}

/** Cuánto ocupa. El guion es un dato: significa que el maestro no lo sabe. */
export function ChipPalets({ carga, dark }: { carga: CargaSalida; dark: boolean }) {
  const { t } = useTranslation()
  if (carga.palets === null || carga.palets === undefined) {
    return <Chip tono="neutro" dark={dark} title={t('muelles.chips.paletsSinDato')}>— pal</Chip>
  }
  const parcial = (carga.paletsSinMaestro ?? 0) > 0
  return (
    <Chip
      tono={parcial ? 'aviso' : 'neutro'}
      dark={dark}
      title={parcial
        ? t('muelles.chips.paletsParcial', { n: carga.paletsSinMaestro })
        : t('muelles.chips.paletsTooltip')}
    >
      {parcial ? '≥' : ''}{carga.palets} pal
    </Chip>
  )
}
